// front/src/preview-mode/adPerformance/dataset.ts
//
// Healthcare campaigns are derived live from the shared synthetic session
// graph (front/src/preview-mode/graph/) — the same sessions Web Analytics
// reads — so per-campaign conversion counts reconcile by construction.
// Pharma has no session graph (see handlers/funnels.ts's precedent), so its
// campaigns instead reuse the already-reconciled Zephyra figures from
// ../attribution/dataset.ts rather than inventing a second, disconnected
// number set. Spend is a flat CPC-per-click heuristic; revenue is a flat
// modeled-value-per-conversion heuristic used only to make ROAS non-zero —
// neither is meant to reconcile with a real ad platform (see ANA-1003's
// Non-goals on not building a new spend model).
import {
  CampaignMetricType,
  CampaignStatus,
  CampaignTimeSeriesMetricName,
  FunnelConversionType,
  type AppointmentTimelineEntry,
  type CampaignDevice,
  type CampaignMetric,
  type CampaignMetricsForFunnelConversions,
  type DeviceEvent,
  type GetDevicesForCampaignsResponse,
  type MarketingPerformanceAnalysisCustomer,
  type MarketingPerformanceAnalysisResult,
} from "@/MarketingPerformanceAnalysis/types/MarketingPerformanceAnalysis";

import type { DateRangeWire } from "@/api/analytics";

import { getAttributionDataset } from "../attribution/dataset";
import { getPreviewGraph } from "../graph";
import { parseWireDateRange } from "../graph/web-analytics";
import type { PreviewProfileKey } from "../profiles";

const CPC_CENTS = 250;
const REVENUE_PER_CONVERSION_CENTS: Record<PreviewProfileKey, number> = {
  healthcare: 15000, // $150 modeled appointment value
  pharma: 8500, // $85 modeled prescription-fill value
};

// The fraction of booked appointments that go on to be attended. Every
// "booked vs. attended" view — the campaign table's funnel rows, the device
// list's status column, and the appointment timeline's attendedAt — derives
// from this single constant via isAttended() so they agree on the same
// ratio instead of three independently-chosen magic numbers drifting apart.
const ATTENDED_OF_BOOKED_RATIO = 0.75;

// index is the 0-based position of this booking among a campaign's booked
// appointments (in whatever order they're generated/encountered — device
// list, appointment timeline, etc.). `% 4 !== 0` marks exactly 3 of every 4
// as attended, i.e. ATTENDED_OF_BOOKED_RATIO.
function isAttended(indexAmongBooked: number): boolean {
  return indexAmongBooked % 4 !== 0;
}

// Exact count of isAttended()===true among the first `n` booked items — i.e.
// how many indices in [0, n) are NOT a multiple of 4. Aggregate views (the
// campaign funnel's ATTENDED row) must use this closed form rather than
// Math.round(n * ATTENDED_OF_BOOKED_RATIO): the two can disagree by one for
// campaigns whose booked count isn't a multiple of 4 (e.g. 13 vs. 12
// attended out of 17), which breaks the device list drilling down from the
// funnel table exactly reconciling with it.
function attendedCountOf(n: number): number {
  return n - Math.ceil(n * (1 - ATTENDED_OF_BOOKED_RATIO));
}

export type AdPerformanceDateRange = { start: number; end: number };

// Spreads `count` timestamps evenly across [windowStart, windowEnd], oldest
// first. Used for every synthetic (non-session-backed) per-conversion
// timestamp series - pharma's device list and both profiles' appointment
// timeline - so a date-range filter has real variation to filter against
// instead of every entry clustering within the last few hours of the
// window (which made the date filter a no-op and the duration columns
// permanently blank, since "today" is excluded from every preset range).
function spreadTimestampsAcrossWindow(
  count: number,
  windowStart: number,
  windowEnd: number,
): number[] {
  if (count <= 0) return [];
  const span = windowEnd - windowStart;
  return Array.from({ length: count }, (_, i) =>
    Math.round(windowStart + (span * (i + 0.5)) / count),
  );
}

function inRange(
  timestamp: number,
  dateRange?: AdPerformanceDateRange,
): boolean {
  return (
    !dateRange || (timestamp >= dateRange.start && timestamp <= dateRange.end)
  );
}

// Parses the wire-shape dateRange every Ad Performance Kind receives into
// timestamps, anchored to the synthetic graph's window (matching how
// preview-mode/graph/web-analytics.ts anchors analytics queries).
export function parseAdPerformanceDateRange(
  dateRange: DateRangeWire,
): AdPerformanceDateRange {
  return parseWireDateRange(dateRange, getPreviewGraph().windowEnd);
}

export interface AdPerformanceCampaign {
  campaignId: string;
  campaignName: string;
  campaignStatus: CampaignStatus;
  conversions: number;
  spendCents: number;
  // Undefined covers paid sources that don't map to either platform the
  // real AdPlatformType enum models (e.g. Bing) — not "unknown".
  platform?: "google_ads" | "meta_ads";
}

// Modest week-over-week growth applied to derive a plausible `prev` baseline
// for numConversions only. Without one, both sides of getAttendedSuccessRate()
// (MarketingPerformanceAnalysis/utils/getAttendedConversionData.ts) see
// prev: null and fall back to a hardcoded success rate of 1 — a branch that
// almost never fires for real accounts (which nearly always have some prior-
// period data), but fires on every single Ad Performance preview row, turning
// the Success Rate column's change badge into a nonsensical "+7,438%"-style
// figure instead of a believable one. 0.85 (≈ +18% current-over-previous)
// echoes the "+18% month-over-month" narrative the Weekly Recommendations
// cards already tell for Zephyra_Brand_Search. customerAcquisitionCost /
// revenue / returnOnAdSpend keep prev: null on purpose — PercentageChange
// already renders a null previous value as "—", which is the correct
// "no prior period" treatment for those fields.
const PREV_PERIOD_GROWTH_FACTOR = 0.85;

function buildFunnelRow(
  conversionType: FunnelConversionType,
  profile: PreviewProfileKey,
  conversions: number,
  spendCents: number,
): CampaignMetricsForFunnelConversions {
  const cacCents = conversions === 0 ? 0 : Math.round(spendCents / conversions);
  const revenueCents = conversions * REVENUE_PER_CONVERSION_CENTS[profile];
  const roasPct =
    spendCents === 0 ? 0 : Math.round((revenueCents / spendCents) * 100);
  return {
    conversionType,
    numConversions: {
      current: conversions,
      prev: Math.max(1, Math.round(conversions * PREV_PERIOD_GROWTH_FACTOR)),
    },
    // Cents, not dollars — the real query layer (useQueryMarketingPerformanceAnalysis)
    // calls convertCentsToDollars() on this field itself.
    customerAcquisitionCost: { current: cacCents, prev: null },
    revenue: { current: revenueCents, prev: null },
    returnOnAdSpend: { current: roasPct, prev: null },
  };
}

// Emits both the BOOKED and ATTENDED funnel rows the real MarketingPerformanceAnalysis
// components read (getAttendedConversionData / getBookedConversionData match on
// `conversionType`). ATTENDED reflects only the subset of booked appointments
// that have gone on to be attended (see ATTENDED_OF_BOOKED_RATIO).
function toFunnelRows(
  profile: PreviewProfileKey,
  bookedConversions: number,
  spendCents: number,
): CampaignMetricsForFunnelConversions[] {
  const attendedConversions = attendedCountOf(bookedConversions);
  return [
    buildFunnelRow(
      FunnelConversionType.BOOKED,
      profile,
      bookedConversions,
      spendCents,
    ),
    buildFunnelRow(
      FunnelConversionType.ATTENDED,
      profile,
      attendedConversions,
      spendCents,
    ),
  ];
}

function titleCase(campaignKey: string): string {
  return campaignKey
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function platformFromUtmSources(
  sourceCounts: Record<string, number>,
): "google_ads" | "meta_ads" | undefined {
  // The synthetic graph picks a paid session's utm.source and utm.campaign
  // independently, so a campaign key's sessions can mix sources. Use
  // whichever source appears most often as the campaign's platform.
  const dominant = Object.entries(sourceCounts).sort(
    (a, b) => b[1] - a[1],
  )[0]?.[0];
  switch (dominant) {
    case "google":
      return "google_ads";
    case "meta":
      return "meta_ads";
    default:
      // e.g. "bing" — a real paid source, but not one of the two platforms
      // AdPlatformType models; leave it unmatched by either specific filter.
      return undefined;
  }
}

// Real session timestamps make this an exact filter, not a heuristic:
// narrowing the date range genuinely drops sessions outside it before
// aggregating, so campaign totals respond to the UI's date filter.
function healthcareCampaigns(
  dateRange?: AdPerformanceDateRange,
): AdPerformanceCampaign[] {
  const graph = getPreviewGraph();
  const byCampaign = new Map<
    string,
    {
      conversions: number;
      clicks: number;
      sourceCounts: Record<string, number>;
    }
  >();
  for (const session of graph.sessions) {
    if (session.channel !== "paid" || !session.utm) continue;
    if (!inRange(session.startedAt, dateRange)) continue;
    const key = session.utm.campaign;
    const entry = byCampaign.get(key) ?? {
      conversions: 0,
      clicks: 0,
      sourceCounts: {},
    };
    entry.clicks += 1;
    if (session.outcome === "converting") entry.conversions += 1;
    entry.sourceCounts[session.utm.source] =
      (entry.sourceCounts[session.utm.source] ?? 0) + 1;
    byCampaign.set(key, entry);
  }
  return Array.from(byCampaign.entries()).map(([campaign, stats]) => ({
    campaignId: campaign,
    campaignName: titleCase(campaign),
    campaignStatus: CampaignStatus.ENABLED,
    conversions: stats.conversions,
    spendCents: stats.clicks * CPC_CENTS,
    platform: platformFromUtmSources(stats.sourceCounts),
  }));
}

// Pharma has no per-session data to filter (see file header), so a narrower
// date range instead counts how many of the campaign's evenly-spread
// synthetic conversion timestamps (the same basis pharmaDevicesForCampaign
// uses) fall inside it, and scales spend by the same factor - so CPA/ROAS
// stay exactly as reconciled, and the campaign row's conversion count stays
// consistent with what the same campaign's device-list drill-down shows for
// that range, rather than inventing a second, disconnected number set.
function pharmaCampaigns(
  dateRange?: AdPerformanceDateRange,
): AdPerformanceCampaign[] {
  const graph = getPreviewGraph();
  return getAttributionDataset("pharma").campaigns.map((c) => {
    const conversions = dateRange
      ? spreadTimestampsAcrossWindow(
          c.conversions,
          graph.windowStart,
          graph.windowEnd,
        ).filter((t) => inRange(t, dateRange)).length
      : c.conversions;
    const scale = c.conversions === 0 ? 0 : conversions / c.conversions;
    return {
      campaignId: c.key,
      campaignName: c.label,
      campaignStatus: CampaignStatus.ENABLED,
      conversions,
      spendCents: Math.round(c.spendCents * scale),
      platform:
        c.platform === "google_ads" || c.platform === "meta_ads"
          ? c.platform
          : undefined,
    };
  });
}

export function getAdPerformanceCampaigns(
  profile: PreviewProfileKey,
  dateRange?: AdPerformanceDateRange,
): AdPerformanceCampaign[] {
  return profile === "pharma"
    ? pharmaCampaigns(dateRange)
    : healthcareCampaigns(dateRange);
}

export function getAdPerformanceCustomer(
  profile: PreviewProfileKey,
): MarketingPerformanceAnalysisCustomer {
  const graph = getPreviewGraph();
  return {
    hasAutomatedCSVUploads: false,
    safeAnalysisDateRangeStart: new Date(graph.windowStart).toISOString(),
    googleAdsDestId: "preview-google-ads",
    metaAdsDestIds: ["preview-meta-ads"],
    bingAdsDestId: profile === "healthcare" ? "preview-bing-ads" : undefined,
  };
}

export function getAdPerformanceResults(
  profile: PreviewProfileKey,
  dateRange?: AdPerformanceDateRange,
): MarketingPerformanceAnalysisResult[] {
  return getAdPerformanceCampaigns(profile, dateRange).map((c) => ({
    campaignName: c.campaignName,
    campaignId: c.campaignId,
    campaignStatus: c.campaignStatus,
    // Cents, not dollars — see buildFunnelRow's comment.
    campaignSpend: { current: c.spendCents, prev: null },
    funnel: toFunnelRows(profile, c.conversions, c.spendCents),
    hasBookedRevenue: true,
    hasAttendedRevenue: true,
  }));
}

function healthcareDevicesForCampaign(
  campaignId: string,
  dateRange?: AdPerformanceDateRange,
): CampaignDevice[] {
  const graph = getPreviewGraph();
  // Keyed by deviceId — a device with multiple paid sessions in this
  // campaign would otherwise appear more than once with a duplicate
  // deviceId, which DeviceTable uses as its React row key. Keep only the
  // row with the latest lastEventTime.
  const byDevice = new Map<string, CampaignDevice>();
  let bookedIndex = 0;
  for (const session of graph.sessions) {
    if (session.channel !== "paid" || session.utm?.campaign !== campaignId)
      continue;
    if (!inRange(session.startedAt, dateRange)) continue;
    const lastEvent = session.events[session.events.length - 1];
    const booked = session.outcome === "converting";
    if (booked) bookedIndex += 1;
    const row: CampaignDevice = {
      deviceId: session.deviceID,
      lastEventTime: new Date(
        lastEvent?.timestamp ?? session.startedAt,
      ).toISOString(),
      bookedAppointment: booked,
      // isAttended()/ATTENDED_OF_BOOKED_RATIO, not "every booked session is
      // attended" — the campaign table's funnel only counts ~75% of booked
      // conversions as attended, so this device list must match it (Finding
      // B: it previously said 100%, contradicting the table it drills down
      // from).
      attendedAppointment: booked && isAttended(bookedIndex - 1),
    };
    const existing = byDevice.get(row.deviceId);
    if (!existing || row.lastEventTime > existing.lastEventTime) {
      byDevice.set(row.deviceId, row);
    }
  }
  return Array.from(byDevice.values());
}

function pharmaDevicesForCampaign(
  campaignId: string,
  conversions: number,
  dateRange?: AdPerformanceDateRange,
): CampaignDevice[] {
  const graph = getPreviewGraph();
  const timestamps = spreadTimestampsAcrossWindow(
    conversions,
    graph.windowStart,
    graph.windowEnd,
  );
  return timestamps
    .map((t, i) => ({
      deviceId: `${campaignId}-device-${i + 1}`,
      lastEventTime: new Date(t).toISOString(),
      bookedAppointment: true,
      attendedAppointment: isAttended(i),
      timestamp: t,
    }))
    .filter((d) => inRange(d.timestamp, dateRange))
    .map(({ timestamp: _timestamp, ...device }) => device);
}

export function getAdPerformanceDevices(
  profile: PreviewProfileKey,
  campaignId: string,
  dateRange?: AdPerformanceDateRange,
): GetDevicesForCampaignsResponse {
  const devices =
    profile === "pharma"
      ? pharmaDevicesForCampaign(
          campaignId,
          // Unfiltered total — pharmaDevicesForCampaign applies dateRange
          // itself, against the same spread timestamps pharmaCampaigns()
          // uses to scale its own conversions count for this range.
          pharmaCampaigns().find((c) => c.campaignId === campaignId)
            ?.conversions ?? 0,
          dateRange,
        )
      : healthcareDevicesForCampaign(campaignId, dateRange);
  return {
    pagination: {
      currentPage: 1,
      nextPage: 1,
      totalPages: 1,
      totalRecords: devices.length,
    },
    devices,
  };
}

export function getAdPerformanceDeviceEvents(deviceId: string): DeviceEvent[] {
  const graph = getPreviewGraph();
  const session = graph.sessions.find((s) => s.deviceID === deviceId);
  if (session) {
    return session.events.map((e) => ({
      event: e.eventName,
      eventTime: new Date(e.timestamp).toISOString(),
      properties: { path: e.path },
    }));
  }
  // Pharma synthetic device ids aren't backed by a real session — return a
  // plausible fixed click-to-booking sequence instead.
  const now = graph.windowEnd;
  return [
    {
      event: "ad_click",
      eventTime: new Date(now - 3 * 60 * 60 * 1000).toISOString(),
      properties: {},
    },
    {
      event: "landing_page_view",
      eventTime: new Date(now - 3 * 60 * 60 * 1000 + 5000).toISOString(),
      properties: {},
    },
    {
      event: "checkout_started",
      eventTime: new Date(now - 2 * 60 * 60 * 1000).toISOString(),
      properties: {},
    },
    {
      event: "appointment_booked",
      eventTime: new Date(now - 60 * 60 * 1000).toISOString(),
      properties: {},
    },
  ];
}

// get_ehr_marketing_appointment_timeline takes no dateRange (see
// GetAppointmentTimelineRequest) - the real UI filters the full returned
// list client-side (computeCampaignDurations), so this always returns every
// appointment across the full graph window, unfiltered, for both profiles.
// bookedAt is spread evenly across [windowStart, windowEnd] rather than
// clustered in the last few hours of the window: clustering near "now" put
// every entry outside the 7D/30D/etc. presets (which all exclude "today",
// see design-system/DateFilter), so the Click→Book/Book→Attend/Click→Attend
// columns were permanently blank and never responded to the date filter
// (Finding C).
export function getAdPerformanceAppointmentTimeline(
  profile: PreviewProfileKey,
  campaignId?: string,
): AppointmentTimelineEntry[] {
  const campaigns = getAdPerformanceCampaigns(profile).filter(
    (c) => !campaignId || c.campaignId === campaignId,
  );
  const graph = getPreviewGraph();
  const entries: AppointmentTimelineEntry[] = [];
  let seq = 0;
  for (const campaign of campaigns) {
    const bookedTimestamps = spreadTimestampsAcrossWindow(
      campaign.conversions,
      graph.windowStart,
      graph.windowEnd,
    );
    for (let i = 0; i < bookedTimestamps.length; i++) {
      seq += 1;
      const bookedAt = bookedTimestamps[i];
      entries.push({
        externalId: `${campaign.campaignId}-appt-${seq}`,
        clickDate: new Date(bookedAt - 30 * 60 * 1000).toISOString(),
        bookedAt: new Date(bookedAt).toISOString(),
        attendedAt: isAttended(i)
          ? new Date(bookedAt + 24 * 60 * 60 * 1000).toISOString()
          : null,
        campaignName: campaign.campaignName,
      });
    }
  }
  return entries;
}

export function getAdPerformanceTimeSeries(
  profile: PreviewProfileKey,
  campaignId: string,
  dateRange?: AdPerformanceDateRange,
): CampaignMetric[] {
  const campaign = getAdPerformanceCampaigns(profile, dateRange).find(
    (c) => c.campaignId === campaignId,
  );
  if (!campaign) return [];
  // The real backend's timeseries (back/analytics/cac-analysis/timeseries.go)
  // computes Attended Appointments/CAC/ROAS off attended counts and attended
  // revenue, not booked — so use the ATTENDED funnel row here too.
  const [, attendedRow] = toFunnelRows(
    profile,
    campaign.conversions,
    campaign.spendCents,
  );
  const graph = getPreviewGraph();
  const rangeStart = dateRange?.start ?? graph.windowStart;
  const rangeEnd = dateRange?.end ?? graph.windowEnd;
  const rangeMs = Math.max(rangeEnd - rangeStart, 1);
  const weekMs = 7 * 24 * 60 * 60 * 1000;
  // Roughly one bucket per week of the selected range (min 1, capped so a
  // 12-month view doesn't render 52 near-empty points) — buckets span the
  // actual requested range instead of always the fixed 4-week graph window,
  // so switching between 7D/30D/3M/etc. changes both the totals (via the
  // date-filtered campaign above) and where the points fall (Finding D).
  const weeks = Math.min(12, Math.max(1, Math.round(rangeMs / weekMs)));
  const perWeek = (total: number) =>
    Array.from({ length: weeks }, (_, i) => {
      // Symmetric 0.7-1.3 spread around 1.0 for any bucket count, so the
      // "gentle upward trend" flavor is kept without biasing the sum away
      // from `total` (factors still average to exactly 1.0).
      const factor = weeks === 1 ? 1 : 0.7 + (0.6 * i) / (weeks - 1);
      return {
        periodStart: new Date(rangeStart + (i * rangeMs) / weeks).toISOString(),
        value: Math.round((total / weeks) * factor),
      };
    });
  return [
    {
      name: CampaignTimeSeriesMetricName.ATTENDED_APPOINTMENTS,
      currentTotal: attendedRow.numConversions.current,
      type: CampaignMetricType.Count,
      timeSeries: perWeek(attendedRow.numConversions.current),
    },
    {
      name: CampaignTimeSeriesMetricName.CAC,
      currentTotal: attendedRow.customerAcquisitionCost.current,
      type: CampaignMetricType.Dollar,
      timeSeries: perWeek(attendedRow.customerAcquisitionCost.current),
    },
    {
      name: CampaignTimeSeriesMetricName.ROAS,
      currentTotal: attendedRow.returnOnAdSpend.current,
      type: CampaignMetricType.Percentage,
      timeSeries: perWeek(attendedRow.returnOnAdSpend.current),
    },
  ];
}
